import { BlogSection } from '@/types/blog';

export const fakeNewsSection: BlogSection = {
  id: 'fake-news-erkennen',
  title: 'Fake News erkennen: Kritisches Denken im Finanzbereich',
  content: `
    <p>
      In der Finanzwelt können Fehlinformationen und manipulierte Nachrichten besonders schädlich sein. Wie erkennst du Fake News und unseriöse Quellen?
    </p>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
      <div class="bg-joerg-50 p-4 rounded-lg border border-joerg-100">
        <h5 class="font-medium text-joerg-800 mb-2">So erkennst du seriöse Quellen:</h5>
        <ul class="space-y-1 text-sm">
          <li>Transparente Autorenschaft und Qualifikationen</li>
          <li>Klare Quellenangaben für Behauptungen</li>
          <li>Ausgewogene Darstellung mit Vor- und Nachteilen</li>
          <li>Trennung von Fakten und Meinungen</li>
          <li>Offenlegung von möglichen Interessenkonflikten</li>
          <li>Aktualisierung bei Fehlern</li>
        </ul>
      </div>
      
      <div class="bg-joerg-50 p-4 rounded-lg border border-joerg-100">
        <h5 class="font-medium text-joerg-800 mb-2">Diese Anzeichen sollten dich misstrauisch machen:</h5>
        <ul class="space-y-1 text-sm">
          <li>Reißerische Überschriften und emotionale Sprache</li>
          <li>Unspezifische "Experten" oder "Studien" ohne Namen</li>
          <li>Fehlende oder mangelhafte Quellenangaben</li>
          <li>Extreme, einseitige Darstellungen</li>
          <li>Ungewöhnliche URL-Adressen oder Seitendesigns</li>
          <li>Viele Rechtschreib- und Grammatikfehler</li>
        </ul>
      </div>
    </div>
    
    <p>
      Wichtig: Eine einzige Quelle sollte nie die Grundlage für wichtige Finanzentscheidungen sein. Suche immer nach mehreren unabhängigen Quellen und prüfe wissenschaftliche Belege für Behauptungen.
    </p>
    
    <div class="my-6 bg-joerg-50 p-5 rounded-lg border border-joerg-100">
      <h4 class="text-joerg-800 font-medium mb-3">Prüfe deine Quellen mit der SMELL-Methode</h4>
      <ul class="space-y-2">
        <li><strong>S</strong>ource (Quelle): Wer steht hinter der Information?</li>
        <li><strong>M</strong>otivation (Motivation): Welches Interesse könnte hinter der Veröffentlichung stehen?</li>
        <li><strong>E</strong>vidence (Belege): Welche Beweise/Daten werden angeführt und sind diese überprüfbar?</li>
        <li><strong>L</strong>ogic (Logik): Sind die Schlussfolgerungen logisch nachvollziehbar?</li>
        <li><strong>L</strong>eft out (Ausgelassen): Welche wichtigen Informationen fehlen möglicherweise?</li>
      </ul>
    </div>
    
    <h3 class="text-xl font-semibold mt-10 mb-4">Finanzielles Gaslighting: Wenn Manipulation deine Wahrnehmung verzerrt</h3>
    
    <p>
      "Gaslighting" ist eine besonders heimtückische Form der Manipulation, bei der deine Wahrnehmung der Realität gezielt infrage gestellt wird. Im Finanzkontext kann dies verschiedene Formen annehmen:
    </p>
    
    <div class="my-6 bg-primary/5 p-5 rounded-lg border border-primary/20">
      <h4 class="text-primary font-medium mb-3">Anzeichen für finanzielles Gaslighting:</h4>
      <ul class="space-y-2">
        <li><strong>Leugnen von Fakten:</strong> "Diese Gebühren habe ich nie erwähnt" (obwohl sie eindeutig kommuniziert wurden)</li>
        <li><strong>Verdrehen von Aussagen:</strong> "Du hast mir gesagt, du willst höhere Risiken eingehen" (obwohl das Gegenteil der Fall war)</li>
        <li><strong>Untergrabung deiner Kompetenz:</strong> "Von Finanzen verstehst du doch nichts, überlass das lieber mir"</li>
        <li><strong>Abwertung deiner Bedenken:</strong> "Du bist viel zu ängstlich/emotional, um rationale Finanzentscheidungen zu treffen"</li>
        <li><strong>Isolierung:</strong> "Sprich mit niemandem sonst darüber, die verstehen unser Konzept nicht"</li>
      </ul>
    </div>
    
    <p>
      Gaslighting zielt darauf ab, dich an deiner eigenen Wahrnehmung und Urteilsfähigkeit zweifeln zu lassen, damit du dich den Empfehlungen des Manipulators fügst. Dies kann von unseriösen Finanzberatern, Finfluencern oder sogar Partnern in Finanzbeziehungen praktiziert werden.
    </p>
    
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
      <div class="bg-joerg-50 p-4 rounded-lg border border-joerg-100">
        <h5 class="font-medium text-joerg-800 mb-2">So schützt du dich vor finanziellem Gaslighting:</h5>
        <ul class="space-y-1 text-sm">
          <li>Führe ein Finanztagebuch mit allen wichtigen Entscheidungen und Gesprächen</li>
          <li>Bestehe auf schriftlicher Kommunikation bei wichtigen Finanzthemen</li>
          <li>Hole eine zweite Meinung von unabhängigen Experten ein</li>
          <li>Vertraue deiner Wahrnehmung und deinem gesunden Menschenverstand</li>
          <li>Bilde dich kontinuierlich in Finanzthemen weiter, um selbstbewusster zu werden</li>
        </ul>
      </div>
      
      <div class="bg-joerg-50 p-4 rounded-lg border border-joerg-100">
        <h5 class="font-medium text-joerg-800 mb-2">Alarmsignale in der Kommunikation:</h5>
        <ul class="space-y-1 text-sm">
          <li>"Das habe ich nie gesagt" (obwohl es dokumentiert ist)</li>
          <li>"Du erinnerst dich falsch" (ohne sachliche Argumente)</li>
          <li>"Du bist zu emotional/unerfahren, um das zu verstehen"</li>
          <li>"Wenn du mir nicht vertraust, kann ich dir nicht helfen"</li>
          <li>"Niemand sonst wird dir bei diesen Fragen so helfen können wie ich"</li>
          <li>"Alle erfolgreichen Menschen machen das so"</li>
        </ul>
      </div>
    </div>
  `
};
